// ============================================================================
// CreateFamily.jsx — FAZA 5: krijimi i një familjeje të re
//
// Rruga: /start (shih main.jsx). Krejt e ndarë, si PlatformAdmin.jsx dhe
// AcceptInvite.jsx — Auth i vetin, s'ndan gjendje me App.jsx.
//
// Rrjedha:
//   1. Kërkon sesion Auth (Google/Apple/Email) — askush s'krijon familje
//      anonimisht.
//   2. Emri i familjes → sugjerohet automatikisht një slug i lirë
//      (rpc_suggest_family_slug, prapashtesë e rastësishme numerike — shih
//      schema.sql). FIX (kërkesë e userit): fusha e slug-ut tani është
//      VETËM-LEXIM (grared, jo e klikueshme) — përdoruesi s'e redakton më
//      me dorë, thjesht e sheh dhe vazhdon.
//   3. Emri/mbiemri/datëlindja e VETË krijuesit → bëhet automatikisht
//      admini i parë i familjes (rpc_create_family — e njëjta logjikë si
//      bootstrap-i i Halitit, shih FAZA 3).
//   4. Sukses → link direkt te /f/{slug}, ku familja e sapokrijuar tashmë
//      funksionon normalisht (identifikimi e njeh vetë llogarinë e lidhur).
// ============================================================================

import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BackableCard } from "./BackablePage.jsx";
import { supabase } from "./supabaseClient";
import HyrjaEJashtme from "./HyrjaEJashtme.jsx"; // ekrani i hyrjes — i njëjti si ballina
import DateField from "./DateField"; // shkrim i lirë i datës + kalendari i sistemit
import { shenoGjurmen } from "./gjurma.js"; // PROVAT1 — gjurma e llogarisë pa familje

// FAZA 4 (SHTRESA JURIDIKE) — versioni aktual i Kushteve TË PLATFORMËS
// (jo dokumenti i brendshëm i familjes, ai mbetet i ndarë, shih App.jsx
// termsGateOpen). Ndrysho këtë STRING sa herë ndryshojnë faqet /terms ose
// /privacy në një mënyrë kuptimplotë — çdo familje e re do ta "pranojë"
// versionin AKTUAL, dhe rpc_create_family e ruan atë vlerë saktësisht.
const PLATFORM_TERMS_VERSION = "draft-2026-08-03";

export default function CreateFamily() {
  // 4 shtator 2026 — faqja ku nis një familje e re ishte krejt shqip, edhe te
  // ndërfaqja angleze. Çelësat janë vetë fjalitë, si kudo tjetër.
  const { t } = useTranslation();
  useEffect(() => { shenoGjurmen("start_page", null, (typeof document !== "undefined" && document.referrer) || null, true, null); }, []);
  const [session, setSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(true);

  // HYRJA (Google/Apple/email) JETON TE `HyrjaEJashtme.jsx` — 3 shtator 2026.
  // Këtu qëndronin gjashtë gjendje dhe dy funksione që bënin pikërisht atë që
  // bën ballina, vetëm me pamje tjetër. Ato u hoqën bashkë me formën.
  const [familyName, setFamilyName] = useState("");
  const [slug, setSlug] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [createError, setCreateError] = useState("");
  const [createBusy, setCreateBusy] = useState(false);
  const [created, setCreated] = useState(null); // { slug }
  const [termsAccepted, setTermsAccepted] = useState(false); // FAZA 4

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setSessionLoading(false);
    });
    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => setSession(newSession));
    return () => listener.subscription.unsubscribe();
  }, []);

  // Sugjeron (dhe RIGJENERON) slug-un automatikisht sa herë ndryshon emri i
  // familjes — fusha e slug-ut tani është vetëm-lexim (shih render më
  // poshtë), përdoruesi s'e prek më me dorë, kështu që s'ka nevojë të
  // gjurmohet nëse është "prekur" (slugTouched, hequr).
  useEffect(() => {
    if (!familyName.trim()) { setSlug(""); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase.rpc("rpc_suggest_family_slug", { p_base: familyName });
      if (data) setSlug(data);
    }, 400);
    return () => clearTimeout(timer);
  }, [familyName]);

  async function handleCreate(e) {
    e.preventDefault();
    setCreateError("");
    if (!familyName.trim() || !slug.trim() || !firstName.trim() || !lastName.trim()) {
      setCreateError(t("Fill in all the required fields."));
      return;
    }
    if (!termsAccepted) {
      setCreateError(t("You must accept the Terms of Service and the Privacy Policy to continue."));
      return;
    }
    setCreateBusy(true);
    const { data, error } = await supabase.rpc("rpc_create_family", {
      p_family_name: familyName.trim(),
      p_desired_slug: slug.trim(),
      p_first_name: firstName.trim(),
      p_last_name: lastName.trim(),
      p_birth_date: birthDate || null,
      p_platform_terms_version: PLATFORM_TERMS_VERSION,
    });
    setCreateBusy(false);
    if (error) { setCreateError(error.message); shenoGjurmen("create_family", null, familyName.trim(), false, error.message); return; }
    const row = Array.isArray(data) ? data[0] : data;
    shenoGjurmen("create_family", null, familyName.trim(), true, (row && row.slug) || null, (row && row.family_id) || null);
    setCreated({ slug: row.slug });
  }

  if (sessionLoading) return <CenteredCard title={t("Loading…")}>…</CenteredCard>;

  if (created) {
    return (
      <CenteredCard title={t("The family has been created! ✓")}>
        <p style={{ marginBottom: 16 }}>{t("You are now the first admin. Keep this link — it is your family's address:")}</p>
        <p style={{ marginBottom: 20 }}><a href={`/f/${created.slug}`}>{window.location.origin}/f/{created.slug}</a></p>
        <a href={`/f/${created.slug}`} style={{ ...buttonStyle, display: "inline-block", textDecoration: "none", textAlign: "center" }}>{t("Open my tree")}</a>
      </CenteredCard>
    );
  }

  // E NJËJTA DERË SI BALLINA — stema, ngjyrat dhe variantin i cakton admini te
  // Admin Center → Ekrani i hyrjes. Këtu vetëm i themi PSE erdhi njeriu.
  if (!session) {
    return (
      <HyrjaEJashtme
        celesiTitullit="hyrje.startTitle"
        celesiHyrjes="hyrje.startIntro"
      />
    );
  }

  return (
    <CenteredCard title={t("Start your family tree")}>
      <form onSubmit={handleCreate} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <Label>{t("Family surname")}</Label>
          <input value={familyName} onChange={(e) => setFamilyName(e.target.value)} placeholder={t("e.g. Berisha")} required style={inputStyle} />
          {familyName.trim() && (
            <p style={{ fontSize: 11.5, color: "#999", marginTop: 4 }}>{t("Will be shown as:")} <strong>{t("The {{emri}} family", { emri: familyName.trim() })}</strong></p>
          )}
        </div>
        <div>
          <Label>{t("The family address (generated automatically)")}</Label>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#666" }}>
            <span>{window.location.origin}/f/</span>
            <input
              value={slug}
              readOnly
              tabIndex={-1}
              style={{ ...inputStyle, flex: 1, background: "#f3f4f6", color: "#888", cursor: "not-allowed" }}
            />
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}><Label>{t("Your first name")}</Label><input value={firstName} onChange={(e) => setFirstName(e.target.value)} required style={inputStyle} /></div>
          <div style={{ flex: 1 }}><Label>{t("Your last name")}</Label><input value={lastName} onChange={(e) => setLastName(e.target.value)} required style={inputStyle} /></div>
        </div>
        <div>
          <Label>{t("Your date of birth (optional)")}</Label>
          <DateField value={birthDate} onChange={setBirthDate} inputStyle={inputStyle} />
        </div>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, color: "#444" }}>
          <input type="checkbox" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} style={{ marginTop: 2 }} />
          <span>
            {t("I accept the")} <a href="/terms" target="_blank" rel="noopener noreferrer">{t("Terms of Service")}</a> {t("and the")}{" "}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">{t("Privacy Policy")}</a>.
          </span>
        </label>
        {createError && <div style={{ color: "#b91c1c", fontSize: 13 }}>{createError}</div>}
        <button type="submit" disabled={createBusy || !termsAccepted} style={buttonStyle}>{createBusy ? t("Creating…") : t("Create the family")}</button>
        <p style={{ fontSize: 11, color: "#999" }}>{t("You will automatically become the first admin of this tree.")}</p>
      </form>
    </CenteredCard>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>{children}</div>;
}

// FIX (kërkesë e userit — "te Bashkohu me kod / Krijo familje s'punon as back
// button as swipe back"): këto janë faqe TË VEÇANTA (rrugë /join, /start), jo
// modale brenda App.jsx, ndaj s'i mbulonte sistemi i back-ut i pemës. Tani
// çdo gjendje e faqes ka: (1) shigjetë "← Kthehu" lart, (2) swipe djathtas
// për t'u kthyer, (3) butonin fizik Back të Android-it. Kthimi përdor
// history.back() nëse ka histori brenda app-it, përndryshe shkon te "/".

// Korniza e perbashket e faqeve te jashtme (back + swipe + Escape) —
// nje zbatim i vetem, i importuar; jo kopje per cdo faqe.
function CenteredCard(props) { return <BackableCard width={420} {...props} />; }

// `background` dhe `color` shkruhen SHPREHIMISHT. Pa to, fusha e trashëgon
// ngjyrën e tekstit nga kartela (preflight-i i Tailwind-it: `input {color:
// inherit}`) por sfondin ia pikturon shfletuesi sipas `color-scheme` të <html>,
// që `designLoader.js` e vë "dark" kur tema është e errët. Ashtu lindi fusha e
// zezë me shkronja të zeza që raportoi pronari më 3 shtator 2026.
const inputStyle = { padding: "10px 12px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, width: "100%", boxSizing: "border-box", background: "#fff", color: "#111827" };
const buttonStyle = { padding: "10px 16px", borderRadius: 8, border: "none", background: "#111827", color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer" };
